import {
  Component,
  computed,
  effect,
  input,
  signal,
  untracked,
} from '@angular/core';

import { CategoriesRepositoryQuery } from '../../domain/repositories/categories.repository';
import {
  AttributeOptionModel,
  CategoryItemModel,
  GroupAttributeModel,
} from '../../domain/models/category.model';
import { GetCategoryUsecase } from '../../data/repositories/categories/usecases/get-category.usecase';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  ConditionOption,
  ListingFilters,
  ListingItemModel,
} from '../../domain/models/listing.model';
import { cleanObject } from '../../lib/clean-object';
import { Router, RouterLink } from '@angular/router';
import { ListingFiltersParserUtility } from '../../data/utilities/listing-filters-parser/listing-filters-parser.utility';
import { GetListingsUsecase } from '../../data/repositories/listings/usecases/get-listings.usecase';
import { ListingsComponent } from './components/listings/listings.component';
import { CheckboxMultiselectFormFieldComponent } from '../shared/components/checkbox-multiselect-form-field/checkbox-multiselect-form-field.component';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { FormFieldComponent } from '../shared/components/form-field/form-field.component';
import { RegionCitySelectorComponent } from '../shared/components/region-city-selector/region-city-selector.component';
import { HomeBannersWrapperComponent } from '../shared/components/home-banners-wrapper/home-banners-wrapper.component';
import { Skeleton } from 'primeng/skeleton';
import { MultiSelectModule } from 'primeng/multiselect';
import { DefaultPageMetaTags } from '../../data/services/page-meta-tags/page-meta-tags.service';
import { PageMetaTagsAdapter } from '../../domain/adapters/page-meta-tags.adapter';

const defaultSeoMetaTagsBySlug: Record<string, Partial<DefaultPageMetaTags>> = {
  velosipedi: {
    title: 'Обяви за Велосипеди - Нови и Втора Употреба | na2kolela.bg',
    metaTags: [
      {
        name: 'description',
        content:
          'Разгледай десетки обяви за планински, шосейни и градски велосипеди. Намери перфектното колело за теб - ново или втора ръка. Филтрирай по марка, цена и град!',
      },
      {
        name: 'og:title',
        content: 'Обяви за Велосипеди - Нови и Втора Употреба | na2kolela.bg',
      },
      {
        name: 'og:description',
        content:
          'Разгледай десетки обяви за планински, шосейни и градски велосипеди. Намери перфектното колело за теб - ново или втора ръка. Филтрирай по марка, цена и град!',
      },
      {
        name: 'og:url',
        content: 'https://na2kolela.bg/.com/velosipedi/',
      },
    ],
  },
  'casti-za-velosipedi': {
    title: 'Части за Велосипеди - Обяви и Цени | na2kolela.bg',
    metaTags: [
      {
        name: 'description',
        content:
          'Намери всичко за твоето колело! Обяви за рамки, гуми, спирачки, скорости и всякакви други части за велосипеди. Надгради или ремонтирай своя велосипед лесно.',
      },
      {
        name: 'og:title',
        content: 'Части за Велосипеди - Обяви и Цени | na2kolela.bg',
      },
      {
        name: 'og:description',
        content:
          'Намери всичко за твоето колело! Обяви за рамки, гуми, спирачки, скорости и всякакви други части за велосипеди. Надгради или ремонтирай своя велосипед лесно.',
      },
      {
        name: 'og:url',
        content: 'https://na2kolela.bg/.com/casti-za-velosipedi/',
      },
    ],
  },
  trotinetki: {
    title: 'Тротинетки - Обяви, Нови и Втора Ръка | na2kolela.bg',
    metaTags: [
      {
        name: 'description',
        content:
          'Търсиш електрическа тротинетка? Разгледай обяви за нови и втора ръка тротинетки от марки като Xiaomi, Segway и други. Намери най-добрата оферта за бързо придвижване в града.',
      },
      {
        name: 'og:title',
        content: 'Тротинетки - Обяви, Нови и Втора Ръка | na2kolela.bg',
      },
      {
        name: 'og:description',
        content:
          'Разгледай обяви за нови и втора ръка тротинетки от марки като Xiaomi, Segway и други. Намери най-добрата оферта за бързо придвижване в града.',
      },
      {
        name: 'og:url',
        content: 'https://na2kolela.bg/.com/trotinetki/',
      },
    ],
  },
  'casti-za-trotinetki': {
    title: 'Части за Тротинетки - Обяви и Аксесоари | na2kolela.bg',
    metaTags: [
      {
        name: 'description',
        content:
          'Всичко за твоята тротинетка на едно място. Обяви за батерии, гуми, контролери, калници и други резервни части и аксесоари. Поддържай тротинетката си в перфектно състояние!',
      },
      {
        name: 'og:title',
        content: 'Части за Тротинетки - Обяви и Аксесоари | na2kolela.bg',
      },
      {
        name: 'og:description',
        content:
          'Всичко за твоята тротинетка на едно място. Обяви за батерии, гуми, контролери, калници и други резервни части и аксесоари.',
      },
      {
        name: 'og:url',
        content: 'https://na2kolela.bg/.com/casti-za-trotinetki/',
      },
    ],
  },
};

@Component({
  selector: 'otw-home',
  standalone: true,
  providers: [],
  imports: [
    ListingsComponent,
    ReactiveFormsModule,
    CheckboxMultiselectFormFieldComponent,
    InputTextModule,
    InputNumber,
    ButtonModule,
    FormFieldComponent,
    RegionCitySelectorComponent,
    HomeBannersWrapperComponent,
    Skeleton,
    RouterLink,
    MultiSelectModule,
  ],
  templateUrl: './home.component.html',
})
export class HomeComponent {
  slug = input(''); // slug from url
  categories = this.categoriesRepositoryQuery.categories;
  selectedSlug = computed(() => {
    if (this.slug()) {
      return this.slug();
    }
    // when the slug is missing from url in the initial load, we need to determine it based on the available categories
    const firstCategory: CategoryItemModel | undefined = this.categories()[0];
    if (!firstCategory) return;

    return firstCategory.slug;
  });
  // selectedCategory = signal<CategoryModel | null>(null);
  requiredAttributes = signal<GroupAttributeModel[]>([]);
  listings = signal<ListingItemModel[]>([]);
  isCategoryLoading = signal(false);
  areListingsLoading = signal(false);

  searchForm = this.fb.group({
    price_min: this.fb.control<number | undefined>(undefined, {
      nonNullable: true,
    }),
    price_max: this.fb.control<number | undefined>(undefined, {
      nonNullable: true,
    }),
    regionCity: this.fb.group({
      region: this.fb.control<string>('', { nonNullable: true }),
      city: this.fb.control<string>('', { nonNullable: true }),
    }),
    condition: this.fb.control<ConditionOption[]>(
      [ConditionOption.NEW, ConditionOption.USED],
      {
        nonNullable: true,
      }
    ),
    attributes: this.fb.record<{ [key: string]: string | number }>({}),
  });

  conditionOptions = [
    {
      label: 'Ново',
      value: ConditionOption.NEW,
    },
    {
      label: 'Употребявано',
      value: ConditionOption.USED,
    },
    {
      label: 'Повредено',
      value: ConditionOption.DAMAGED,
    },
  ];

  constructor(
    private categoriesRepositoryQuery: CategoriesRepositoryQuery,
    private getCategoryUsecase: GetCategoryUsecase,
    private listingFiltersParserUtility: ListingFiltersParserUtility,
    private getListingsUseCase: GetListingsUsecase,
    private fb: FormBuilder,
    private router: Router,
    private pageMetaTagsService: PageMetaTagsAdapter
  ) {
    effect(() => {
      if (!this.slug()) return;
      this._updateMetaTagsBySlug(this.slug());
    });
  }

  fetchCategoryEffect = effect(() => {
    // subscribe to slug change
    const categorySlug = this.selectedSlug();
    if (!categorySlug) return;

    // processing, don't track this part can lead to infinite loops
    untracked(() => {
      this.getCategory(categorySlug);
      this.getListings(categorySlug);
    });
  });

  updateAttributeFields(attributes: GroupAttributeModel[]) {
    this.searchForm.controls.attributes.reset();
    attributes.forEach((attribute) => {
      this.searchForm.controls.attributes.addControl(
        attribute.id,
        this.fb.control(null)
      );
    });
  }

  async getListings(slug: string) {
    this.areListingsLoading.set(true);
    const result = await this.getListingsUseCase.execute({
      start: 0,
      end: 8,
      filters: { slug },
    });

    if (result.isOk()) {
      this.listings.set(result.unwrap().listings);
    } else {
      // TODO: set error
    }

    this.areListingsLoading.set(false);
  }

  async getCategory(slug: string) {
    this.isCategoryLoading.set(true);
    const result = await this.getCategoryUsecase.execute(slug);
    if (result.isOk()) {
      const attributes = result
        .unwrap()
        .groups.flatMap((g) => g.attributes)
        .filter((att) => {
          return att.required;
        });

      this.updateAttributeFields(attributes);
      // this.selectedCategory.set(result.unwrap());
      this.requiredAttributes.set(attributes);
    } else if (result.isErr()) {
      this.router.navigate(['/not-found']);
    }
    this.isCategoryLoading.set(false);
  }

  private _toListingFilters(): ListingFilters {
    const formValues = this.searchForm.value;
    const attributes = cleanObject(formValues.attributes || {});
    const { regionCity, ...rest } = formValues;

    return {
      ...rest,
      region: regionCity?.region ?? undefined,
      city: regionCity?.city ?? undefined,
      slug: this.selectedSlug(),
      attributes,
    };
  }

  toOptions(attributeOptions: AttributeOptionModel<any>[]) {
    return attributeOptions.map((option) => ({
      label: option.value,
      value: option.id,
    }));
  }

  onSubmit() {
    const filters = this._toListingFilters();

    const query = this.listingFiltersParserUtility.toQuery({
      start: 0,
      end: 10,
      filters,
    });

    this.router.navigateByUrl(`listing/results?${query}`);
  }

  private _updateMetaTagsBySlug(slug: string) {
    const seoTags: Partial<DefaultPageMetaTags> | undefined =
      defaultSeoMetaTagsBySlug[slug];
    if (!seoTags) return;

    if (seoTags.title) this.pageMetaTagsService.updateTitle(seoTags.title);

    seoTags.metaTags?.forEach((tag) => {
      this.pageMetaTagsService.updateTag(tag);
    });
  }
}
